import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ByoTableColumn, ByoTableRow } from './byoTable.helpers';
import { VirtualizedTable } from '@components/TableNew';
import { TableColumn, TableColumnsContext } from '@components/TableNew/TableColumns';
import { GlobalContext } from '@components/App/App.context';
import { FCX, Portal, Tooltip } from '@datassential/platform-ui-lib';
import KeywordCell from './KeywordCell/KeywordCell';
import TableTypeToggle from './TableTypeToggle';
import './ByoTable.scss';
import { useByoDataContext } from "@pages/Byo/ByoContext";
import { useDefaultFilterData, useFilters, useMappedFiltersToApi } from "@vms/SelectFilters/SelectFiltersContext";
import useByoMenuExamples, {
    ByoMenuExamplesExportRequest,
    SliceParameter,
    UseByoMenuExamplesParams
} from "@apiHooks/useByoMenuExamples";
import ByoMenuExamplePopup from './ByoMenuExamplePopup';
import { getMacStageName } from "@models/MacStage";
import { SlicesEnum } from "@models/ByoTableData";
import Skeleton, { SkeletonShape } from "@components/Skeleton";
import { useByoTableConfig } from '@containers/ByoTableNewContainer/ByoTable/ByoTableConfig';
import ColumnHeader from "@containers/ByoTableNewContainer/ByoTable/ColumnHeader";
import { useTableSortingContext } from "@components/TableNew/TableSorting";
import { Sort } from "@models";

interface ByoTableProps {
    columns: ByoTableColumn[];
    rows: ByoTableRow[];
    isLoading: boolean;
    selectKeyword: (keywordName?: string | null) => void;
    toggleColorSelection: (keywordName: string) => void;
}

const FOOTER_STYLES = {
    fontWeight: 700,
};

const FIRST_COLUMN_WIDTH = 300;
const MEDIUM_COLUMN_WIDTH = 140;
const DEFAULT_COLUMN_WIDTH = 115;

const ROW_HEIGHT = 40;
const HEAD_HEIGHT = 40;
const FOOTER_HEIGHT = 36;
const BASE_HEIGHT_OFFSET = FOOTER_HEIGHT + HEAD_HEIGHT;

const EMPTY_COLUMNS: ByoTableColumn[] = Array(8).fill(null).map((_, index) => ({
    id: 0,
    sliceId: SlicesEnum.None,
    key: `empty col ${index}`,
    name: '',
    sortKey: '',
    footerContent: '',
}));

enum GrowthColumn {
    TwelveMonth = '12_month_trend',
    FourYear = '4_year_trend',
}

const ByoTable: FCX<ByoTableProps> = ({
    rows,
    columns: rawColumns,
    isLoading,
    selectKeyword,
    toggleColorSelection,
}) => {
    const {
        value: sortingValue,
        setValue: setSortingValue,
    } = useTableSortingContext();
    const { getRem } = useContext(GlobalContext);
    const {
        activeAnalyticsType,
    } = useByoDataContext();

    const filtersForApi = useMappedFiltersToApi();
    const defaultFilterData = useDefaultFilterData();
    const { countryCode } = useFilters();

    const [isMenuExamplesPopupOpened, setIsMenuExamplesPopupOpened] = useState(false);
    const [menuExamplesWordData, setMenuExamplesWordData] = useState<UseByoMenuExamplesParams['word']>(null);
    const [menuExamplesSliceData, setMenuExamplesSliceData] = useState<UseByoMenuExamplesParams['sliceParameter']>(null);
    const [activeGrowthColumnId, setActiveGrowthColumnId] = useState<GrowthColumn>(GrowthColumn.FourYear);

    const [keywordNameConfig, macStageConfig, ...dataTableColumnConfigs] = useByoTableConfig({
        rows,
        columns: rawColumns,
        activeAnalyticsType,
    });

    const {
        fetch: fetchMenuExamples,
        fetchExport: fetchMenuExamplesExport,
        downloadUrl: menuExamplesDownloadUrl,
    } = useByoMenuExamples({
        word: menuExamplesWordData,
        filters: filtersForApi,
        fullFilter: { ...defaultFilterData, countryCode } as ByoMenuExamplesExportRequest['fullFilter'],
        sliceParameter: menuExamplesSliceData
            ? { id: menuExamplesSliceData.id, sliceType: menuExamplesSliceData.sliceType, type: activeAnalyticsType }
            : null,
    });

    const columns = useMemo(
        () => {
            if (rawColumns.length === 0) return EMPTY_COLUMNS;
            return [...rawColumns];
        },
        [rawColumns]
    )

    const totalColumn = useMemo(
        () => columns.find(col => col.sliceId === SlicesEnum.Total),
        [columns]
    );

    const oneYearTrendColumn = useMemo(
        () => columns.find(col => col.sliceId === SlicesEnum.OneYearTrend),
        [columns]
    );

    const fourYearTrendColumn = useMemo(
        () => columns.find(col => col.sliceId === SlicesEnum.FourYearTrend),
        [columns]
    );

    const handleCloseMenuExamplesPopup = useCallback(() => {
        setIsMenuExamplesPopupOpened(false);
    }, []);

    const hasTags = useMemo(
        () => rows.some((keyword) => keyword?.isTag),
        [rows]
    );

    const handleCellClick = useCallback(
        (rowIndex: number, column: ByoTableColumn) => {
            if (isLoading || !column.isClickable) return;
            const { color, columnsValues, chartData, ...wordData } = rows[rowIndex];
            setMenuExamplesWordData(wordData);
            setMenuExamplesSliceData({ id: column.id, sliceType: column.sliceId as SliceParameter['sliceType'] });
            setIsMenuExamplesPopupOpened(true);
        },
        [isLoading, rows]
    );

    const keywordNameCell: TableColumn = useMemo(() => ({
        ...keywordNameConfig,
        head: (
            <>
                <TableTypeToggle/>
                {hasTags && (
                    <div className="tag-label is-legend">
                        AI-POWERED TAG
                    </div>
                )}
            </>
        ),
        Value: ({ index }) => (
            <KeywordCell
                keyword={rows[index]}
                selectKeyword={selectKeyword}
                toggleColorSelection={toggleColorSelection}
                openMenuExamples={() => handleCellClick(index, totalColumn as ByoTableColumn)}
            />
        ),
        style: {
            head: {
                padding: 'revert-layer',
            },
            cell: {
                flex: `0 0 ${FIRST_COLUMN_WIDTH}px`,
                position: 'sticky',
                left: 0,
                fontFamily: 'Nunito, sans-serif',
                fontSize: getRem(13),
                zIndex: 10,
                padding: 0,
            },
            footer: {
                textAlign: 'left',
                position: 'sticky',
                left: 0,
                padding: 'revert-layer',
                ...FOOTER_STYLES,
            },
        },
        accentLevel: 1,
    }), [
        hasTags,
        rows,
        keywordNameConfig,
        totalColumn,
        getRem,
        selectKeyword,
        toggleColorSelection,
        handleCellClick
    ]);

    const macStageCell = useMemo(
        (): TableColumn => ({
            ...macStageConfig,
            head: isLoading ? '' : macStageConfig.head,
            Value: ({ index }) => {
                const rowData = rows[index];

                if (rowData.isLoading) {
                    return (
                        <Skeleton
                            shape={SkeletonShape.Text}
                            height={20}
                            radius={4}
                        />
                    );
                }

                return (
                    <div>
                        {rowData?.macStage ? getMacStageName(rowData.macStage) : '-'}
                    </div>
                );
            },
            style: {
                head: { fontWeight: 700 },
                cell: {
                    flex: `1 0 ${getRem(DEFAULT_COLUMN_WIDTH)}px`,
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontSize: getRem(13),
                    paddingLeft: getRem(13),
                    paddingRight: getRem(13),
                    position: 'relative',
                },
                footer: FOOTER_STYLES,
            },
            accentLevel: 1,
        }),
        [rows, getRem, isLoading, macStageConfig]
    );

    const dataTableColumns: TableColumn[] = useMemo(
        () => {
            return columns.map((column, columnIndex) => {
                const hasTooltip = !!column.tooltipTitle
                    || !!column.tooltipDescription
                    || !!column.tooltipImageUrl;

                let isHidden = false;
                let head = column.name;
                let headStyles = {};
                const dropdownColumnStyles = {
                    paddingBottom: getRem(15),
                    paddingTop: getRem(5),
                };

                const dropdownHeader = (
                    <ColumnHeader
                        content="Past Growth:"
                        options={[
                            {
                                id: GrowthColumn.TwelveMonth,
                                title: "12 months",
                                isActive: activeGrowthColumnId === GrowthColumn.TwelveMonth,
                            },
                            {
                                id: GrowthColumn.FourYear,
                                title: "4 years",
                                isActive: activeGrowthColumnId === GrowthColumn.FourYear,
                            },
                        ]}
                        onOptionChange={(id) => setActiveGrowthColumnId(id as GrowthColumn)}
                    />
                );

                if (column.sliceId === SlicesEnum.OneYearTrend) {
                    isHidden = activeGrowthColumnId !== GrowthColumn.TwelveMonth;
                    head = dropdownHeader;
                    headStyles = dropdownColumnStyles;
                }
                else if (column.sliceId === SlicesEnum.FourYearTrend) {
                    isHidden = activeGrowthColumnId !== GrowthColumn.FourYear;
                    head = dropdownHeader;
                    headStyles = dropdownColumnStyles;
                }
                else if (column.sliceId === SlicesEnum.PredictedFourYearTrend) {
                    console.log('Rendering predicted four year trend column');
                    head = (
                        <ColumnHeader
                            content="Predicted Growth:"
                            options={[
                                {
                                    id: GrowthColumn.FourYear,
                                    title: "4 years",
                                    isActive: true,
                                },
                            ]}
                            onOptionChange={() => null}
                        />

                    );
                    headStyles = dropdownColumnStyles;
                }

                return {
                    ...dataTableColumnConfigs[columnIndex],
                    head: isLoading ?
                        ''
                        : <>
                        {head}
                        {hasTooltip && (
                            <Portal>
                                <Tooltip
                                    id={`tooltip-${column.key}`}
                                    place="top"
                                    positionStrategy="fixed"
                                    variant="dark"
                                    className="ByoTable__header-tooltip"
                                    style={{
                                        padding: 0,
                                    }}
                                >
                                    <div className="ByoTable__header-tooltip-content">
                                        {!!column.tooltipTitle && (
                                            <div className="ByoTable__header-tooltip-definition">
                                                {column.tooltipTitle}
                                            </div>
                                        )}
                                        {!!column.tooltipDescription && (
                                            <div className="ByoTable__header-tooltip-examples">
                                                {column.tooltipDescription}
                                            </div>
                                        )}
                                        {!!column.tooltipImageUrl && (
                                            <img src={column.tooltipImageUrl} alt="tooltip"/>
                                        )}
                                    </div>
                                </Tooltip>
                            </Portal>
                        )}
                    </>,
                    isHidden,
                    getHtmlAttributes: (index) => {
                        if (index === undefined && hasTooltip) return {
                            'data-tooltip-id': `tooltip-${column.key}`,
                        };

                        return {};
                    },
                    Value: ({ index }) => {
                        const rowData = rows[index];
                        const cellValue = rowData.columnsValues[columnIndex];

                        if (rowData.isLoading) {
                            return (
                                <Skeleton
                                    shape={SkeletonShape.Text}
                                    height={20}
                                    radius={4}
                                />
                            );
                        }

                        return (
                            <div onClick={() => handleCellClick(index, column)}>
                                {cellValue ?? '-'}
                            </div>
                        );
                    },
                    style: {
                        head: {
                            ...headStyles,
                            fontWeight: 700,
                        },
                        cell: {
                            flex: `1 0 ${getRem(
                                column.isMedium
                                    ? MEDIUM_COLUMN_WIDTH
                                    : DEFAULT_COLUMN_WIDTH
                            )}px`,
                            width: `${getRem(
                                column.isMedium
                                    ? MEDIUM_COLUMN_WIDTH
                                    : DEFAULT_COLUMN_WIDTH
                            )}px`,
                            cursor: column.isClickable ? 'pointer' : undefined,
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: getRem(13),
                            fontFamily: 'Nunito, sans-serif',
                            paddingLeft: getRem(13),
                            paddingRight: getRem(13),
                            position: 'relative',
                        },
                        footer: FOOTER_STYLES,
                    },
                    accentLevel: column.isDark ? 0 : 1,
                };
            });
        },
        [
            columns,
            rows,
            handleCellClick,
            getRem,
            isLoading,
            dataTableColumnConfigs,
            activeGrowthColumnId,
        ]
    );

    useEffect(() => {
        if (oneYearTrendColumn && fourYearTrendColumn) {
            if (sortingValue.id === oneYearTrendColumn.sortKey && activeGrowthColumnId === GrowthColumn.FourYear) {
                setSortingValue({
                    ...sortingValue,
                    id: fourYearTrendColumn.sortKey,
                });
            }
            else if (sortingValue.id === fourYearTrendColumn.sortKey && activeGrowthColumnId === GrowthColumn.TwelveMonth) {
                setSortingValue({
                    ...sortingValue,
                    id: oneYearTrendColumn.sortKey,
                });
            }
        }
    }, [
        activeGrowthColumnId,
        sortingValue,
        setSortingValue,
        oneYearTrendColumn,
        fourYearTrendColumn,
    ]);

    useEffect(() => {
        if (totalColumn) {
            if (!columns.some(c => c.sortKey === sortingValue.id)) {
                setSortingValue({
                    id: totalColumn.sortKey,
                    direction: Sort.Desc,
                });
            }
        }
    }, [
        totalColumn,
        columns,
        sortingValue,
        setSortingValue,
    ]);

    const allTableColumns: TableColumn[] = useMemo(
        () => [
            keywordNameCell,
            macStageCell,
            ...dataTableColumns
        ],
        [keywordNameCell, dataTableColumns, macStageCell]
    );

    const columnsContextValue = useMemo(
        () => ({ data: allTableColumns }),
        [allTableColumns]
    );

    const containerStyles = useMemo(() => ({
        height: rows.length > 0
            ? `${getRem(rows.length * ROW_HEIGHT + BASE_HEIGHT_OFFSET)}px`
            : `${getRem(50)}px`,
    }), [rows.length, getRem]);

    const tableContentWidth = useMemo(() => {
        const dataColumnsWidth = columns.reduce(
            (total, col) => total + (col.isMedium ? MEDIUM_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH),
            0
        );
        return FIRST_COLUMN_WIDTH + dataColumnsWidth;
    }, [columns]);

    return (
        <>
            <div className="ByoTable" style={containerStyles}>
                <TableColumnsContext.Provider value={columnsContextValue}>
                    <VirtualizedTable
                        rowsCount={rows.length}
                        headHeight={getRem(HEAD_HEIGHT)}
                        rowHeight={getRem(ROW_HEIGHT)}
                        footerHeight={getRem(FOOTER_HEIGHT)}
                        contentWidthPx={tableContentWidth}
                        testId="byoTable"
                    />
                </TableColumnsContext.Provider>
            </div>

            <ByoMenuExamplePopup
                isShown={isMenuExamplesPopupOpened}
                close={handleCloseMenuExamplesPopup}
                fetch={fetchMenuExamples}
                fetchExport={fetchMenuExamplesExport}
                downloadUrl={menuExamplesDownloadUrl}
            />
        </>
    );
};

export default memo(ByoTable);
